import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";
import PhoneInput from "react-phone-number-input";
import { Button } from "../../../components/Button/Button.tsx";
import type { User } from "../../types.ts";
import { useUpdateUser } from "../HomeIndex/hooks/useUpdateUser.ts";
import { DeleteAccountDialog } from "../HomeSidebar/DeleteAccountDialog.tsx";
import { SettingsClickableItem } from "./components/SettingsClickableItem.tsx";
import { SettingsContentLayout } from "./components/SettingsContentLayout.tsx";
import { SettingsItemLayout } from "./components/SettingsItemLayout.tsx";
import { SettingsTextItem } from "./components/SettingsTextItem.tsx";
import { MAIL_TO } from "./constants.ts";

export const AccountProfileSettings = ({
  user,
  onUpdate,
}: {
  user: User;
  onUpdate: () => void;
}) => {
  const { user: auth0User } = useAuth0();
  const [firstName, setFirstName] = useState(user.first_name);
  const [lastName, setLastName] = useState(user.last_name);
  const [phoneNumber, setPhoneNumber] = useState(user.phone_number);

  const [isAccountDeletionDialogOpen, setIsAccountDeletionDialogOpen] =
    useState(false);

  const { updateUser, isLoading: isUserUpdateLoading } = useUpdateUser();

  // XXX: To avoid desynchronization between Auth0 and internal user data, we
  // only allow the user to edit their name if it differs from the Auth0 user's
  // name.
  const isFirstNameEditable = auth0User?.given_name !== user.first_name;
  const isLastNameEditable = auth0User?.family_name !== user.last_name;

  return (
    <>
      <SettingsContentLayout
        header={<div className="label-lg-semibold text-primary">Profile</div>}
        items={[
          <div key="text" className="flex-col">
            <SettingsTextItem
              title="First name"
              value={firstName}
              onChange={setFirstName}
              disabled={!isFirstNameEditable}
            />
            <SettingsTextItem
              title="Last name"
              value={lastName}
              onChange={setLastName}
              disabled={!isLastNameEditable}
            />
            <SettingsItemLayout title="Phone number">
              <PhoneInput
                value={phoneNumber ?? undefined}
                onChange={(value) => {
                  setPhoneNumber(value ?? "");
                }}
                international
                withCountryCallingCode
              />
            </SettingsItemLayout>
            <div className="py-200 flex-row items-center justify-end gap-200">
              <Button
                size="md"
                variant="secondary"
                loading={isUserUpdateLoading}
                disabled={
                  !firstName ||
                  !lastName ||
                  (user.first_name === firstName &&
                    user.last_name === lastName &&
                    user.phone_number === phoneNumber)
                }
                onClick={() =>
                  updateUser(
                    {
                      first_name: firstName,
                      last_name: lastName,
                      phone_number: phoneNumber === "" ? null : phoneNumber,
                    },
                    {
                      onSuccess: onUpdate,
                    },
                  )
                }
              >
                Save changes
              </Button>
            </div>
          </div>,
          <div key="email" className="flex-col">
            <SettingsClickableItem
              title="Email"
              value={user.email}
              buttonLink={MAIL_TO + "?subject=Change my Pimento email"}
              buttonText="Request email update"
            />
            <SettingsClickableItem
              title="Password"
              value="Only you know"
              buttonLink={MAIL_TO + "?subject=Change my Pimento password"}
              buttonText="Request password update"
            />
          </div>,
          <div key="danger" className="flex-col">
            <div className="py-600 flex-row justify-between">
              <div className="flex-col gap-100 size-1/2">
                <div className="body-md-semibold text-error-secondary">
                  Danger zone
                </div>
                <div className="text-secondary">
                  Permanently delete the account and remove access from all
                  workspaces.
                </div>
              </div>
              <Button
                size="md"
                variant="error"
                onClick={() => {
                  setIsAccountDeletionDialogOpen(true);
                }}
              >
                Delete my account
              </Button>
            </div>
          </div>,
        ]}
      />
      <DeleteAccountDialog
        key="delete-account"
        isOpen={isAccountDeletionDialogOpen}
        onOpenChange={(open) => setIsAccountDeletionDialogOpen(open)}
      />
    </>
  );
};
